import PropTypes from 'prop-types';
import React from 'react';
import styled, { keyframes } from 'styled-components';

// Spinning animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.gap || '8px'};
`;

const Spinner = styled.div`
  width: ${(props) => props.size || '16px'};
  height: ${(props) => props.size || '16px'};
  border: ${(props) => props.borderWidth || '2px'} solid
    ${(props) =>
      props.trackColor ||
      (props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)')};
  border-top-color: ${(props) =>
    props.color || props.theme?.colors?.primary || '#FA4616'};
  border-radius: 50%;
  animation: ${spin} ${(props) => props.speed || '0.8s'} linear infinite;
`;

const Label = styled.span`
  font-size: ${(props) => props.fontSize || '14px'};
  color: ${(props) =>
    props.labelColor || (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
`;

/**
 * LoadingSpinner - A reusable loading spinner component
 *
 * @param {string} size - Size of the spinner (default: '16px')
 * @param {string} color - Color of the spinner (default: theme primary)
 * @param {string} trackColor - Color of the track (default: theme-aware gray)
 * @param {string} borderWidth - Width of the spinner border (default: '2px')
 * @param {string} speed - Animation duration (default: '0.8s')
 * @param {string} label - Optional text label to display next to spinner
 * @param {string} labelColor - Color of the label text
 * @param {string} gap - Gap between spinner and label (default: '8px')
 * @param {string} className - Additional CSS class
 */
const LoadingSpinner = ({
  size,
  color,
  trackColor,
  borderWidth,
  speed,
  label,
  labelColor,
  gap,
  className,
}) => {
  return (
    <SpinnerWrapper gap={gap} className={className}>
      <Spinner
        size={size}
        color={color}
        trackColor={trackColor}
        borderWidth={borderWidth}
        speed={speed}
      />
      {label && <Label labelColor={labelColor}>{label}</Label>}
    </SpinnerWrapper>
  );
};

LoadingSpinner.propTypes = {
  size: PropTypes.string,
  color: PropTypes.string,
  trackColor: PropTypes.string,
  borderWidth: PropTypes.string,
  speed: PropTypes.string,
  label: PropTypes.string,
  labelColor: PropTypes.string,
  gap: PropTypes.string,
  className: PropTypes.string,
};

export default LoadingSpinner;

// Export individual styled components for custom implementations
export { Spinner, SpinnerWrapper, Label, spin };
